import React, { useEffect, useState } from 'react';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/Button';
import { Trash2, Plus, Minus, ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
export function CartPage() {
  const {
    items,
    removeFromCart,
    updateQuantity,
    clearCart,
    cartTotal
  } = useCart();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    address: '',
    city: '',
    zip: '',
    cardNumber: '',
    expiry: '',
    cvv: ''
  });
  const [processing, setProcessing] = useState(false);
  // SHIFT+P Bypass for Testing
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.shiftKey && e.key.toLowerCase() === 'p') {
        e.preventDefault();
        setFormData({
          name: 'John Contractor',
          email: 'john@contractor.com',
          address: '123 Industrial Way',
          city: 'Builder City',
          zip: '90210',
          cardNumber: '4242 4242 4242 4242',
          expiry: '12/25',
          cvv: '123'
        });
        setTimeout(() => {
          handleCheckout(true);
        }, 500);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [cartTotal]);
  const handleCheckout = (bypass = false) => {
    setProcessing(true);
    setTimeout(() => {
      const orderId = bypass ? 'TEST-' + Math.floor(Math.random() * 10000) : 'ORD-' + Math.floor(Math.random() * 10000);
      clearCart();
      navigate('/thank-you', {
        state: {
          orderId,
          total: cartTotal,
          customerName: formData.name || 'John Contractor'
        }
      });
    }, 1500);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleCheckout(false);
  };
  if (items.length === 0) {
    return <div className="min-h-screen py-20 bg-gray-50 flex items-center justify-center" data-id="element-232">
        <div className="text-center max-w-md" data-id="element-233">
          <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-234">
            <span className="text-4xl" data-id="element-235">🛒</span>
          </div>
          <h1 className="text-3xl font-black text-[#1a1a1a] mb-4" data-id="element-236">
            Your Cart is Empty
          </h1>
          <p className="text-gray-600 mb-8" data-id="element-237">
            Add some professional sprayers to get started.
          </p>
          <Link to="/" data-id="element-238">
            <Button size="lg" data-id="element-239">Shop Products</Button>
          </Link>
        </div>
      </div>;
  }
  return <div className="min-h-screen py-12 bg-gray-50" data-id="element-240">
      <div className="container mx-auto px-4 max-w-6xl" data-id="element-241">
        <Link to="/" className="inline-flex items-center text-gray-600 hover:text-[#E63946] mb-8 transition-colors" data-id="element-242">
          <ArrowLeft className="w-4 h-4 mr-2" data-id="element-243" /> Continue Shopping
        </Link>

        <h1 className="text-3xl md:text-4xl font-black text-[#1a1a1a] mb-8" data-id="element-244">
          Checkout
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-245">
          {/* Order Summary */}
          <div className="bg-white rounded-lg shadow-md p-6 h-fit" data-id="element-246">
            <h2 className="text-xl font-bold text-[#1a1a1a] mb-6" data-id="element-247">
              Order Summary
            </h2>

            <div className="space-y-4 mb-6" data-id="element-248">
              {items.map(item => <div key={item.id} className="flex gap-4 pb-4 border-b border-gray-100 last:border-0" data-id="element-249">
                  <img src={item.image} alt={item.name} className="w-20 h-20 object-cover rounded bg-gray-100" data-id="element-250" />
                  <div className="flex-grow" data-id="element-251">
                    <h3 className="font-bold text-[#1a1a1a] mb-2" data-id="element-252">
                      {item.name}
                    </h3>
                    <div className="flex items-center justify-between" data-id="element-253">
                      <div className="flex items-center gap-2 bg-gray-50 rounded px-2 py-1" data-id="element-254">
                        <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="text-gray-600 hover:text-[#1a1a1a]" data-id="element-255">
                          <Minus size={14} data-id="element-256" />
                        </button>
                        <span className="text-sm font-medium w-6 text-center" data-id="element-257">
                          {item.quantity}
                        </span>
                        <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="text-gray-600 hover:text-[#1a1a1a]" data-id="element-258">
                          <Plus size={14} data-id="element-259" />
                        </button>
                      </div>
                      <div className="flex items-center gap-4" data-id="element-260">
                        <span className="font-bold text-[#1a1a1a]" data-id="element-261">
                          ${(item.price * item.quantity).toLocaleString()}
                        </span>
                        <button onClick={() => removeFromCart(item.id)} className="text-red-500 hover:text-red-700" data-id="element-262">
                          <Trash2 size={16} data-id="element-263" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>)}
            </div>

            <div className="border-t border-gray-200 pt-4 space-y-2" data-id="element-264">
              <div className="flex justify-between text-gray-600" data-id="element-265">
                <span data-id="element-266">Subtotal</span>
                <span data-id="element-267">${cartTotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-gray-600" data-id="element-268">
                <span data-id="element-269">Shipping</span>
                <span className="text-green-600 font-medium" data-id="element-270">FREE</span>
              </div>
              <div className="flex justify-between text-2xl font-black text-[#1a1a1a] pt-2" data-id="element-271">
                <span data-id="element-272">Total</span>
                <span data-id="element-273">${cartTotal.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Checkout Form */}
          <div className="bg-white rounded-lg shadow-md p-6" data-id="element-274">
            <h2 className="text-xl font-bold text-[#1a1a1a] mb-6" data-id="element-275">
              Payment Information
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4" data-id="element-276">
              <div data-id="element-277">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-278">
                  Full Name
                </label>
                <input type="text" required value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FDB913] focus:border-[#FDB913]" placeholder="John Contractor" data-id="element-279" />
              </div>

              <div data-id="element-280">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-281">
                  Email
                </label>
                <input type="email" required value={formData.email} onChange={e => setFormData({
                ...formData,
                email: e.target.value
              })} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FDB913] focus:border-[#FDB913]" placeholder="john@contractor.com" data-id="element-282" />
              </div>

              <div data-id="element-283">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-284">
                  Address
                </label>
                <input type="text" required value={formData.address} onChange={e => setFormData({
                ...formData,
                address: e.target.value
              })} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FDB913] focus:border-[#FDB913]" placeholder="123 Main St" data-id="element-285" />
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-286">
                <div data-id="element-287">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-288">
                    City
                  </label>
                  <input type="text" required value={formData.city} onChange={e => setFormData({
                  ...formData,
                  city: e.target.value
                })} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FDB913] focus:border-[#FDB913]" data-id="element-289" />
                </div>
                <div data-id="element-290">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-291">
                    ZIP Code
                  </label>
                  <input type="text" required value={formData.zip} onChange={e => setFormData({
                  ...formData,
                  zip: e.target.value
                })} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FDB913] focus:border-[#FDB913]" data-id="element-292" />
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6 mt-6" data-id="element-293">
                <h3 className="font-bold text-[#1a1a1a] mb-4" data-id="element-294">Card Details</h3>

                <div className="mb-4" data-id="element-295">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-296">
                    Card Number
                  </label>
                  <input type="text" required value={formData.cardNumber} onChange={e => setFormData({
                  ...formData,
                  cardNumber: e.target.value
                })} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FDB913] focus:border-[#FDB913]" placeholder="4242 4242 4242 4242" data-id="element-297" />
                </div>

                <div className="grid grid-cols-2 gap-4" data-id="element-298">
                  <div data-id="element-299">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-300">
                      Expiry
                    </label>
                    <input type="text" required value={formData.expiry} onChange={e => setFormData({
                    ...formData,
                    expiry: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FDB913] focus:border-[#FDB913]" placeholder="MM/YY" data-id="element-301" />
                  </div>
                  <div data-id="element-302">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-303">
                      CVV
                    </label>
                    <input type="text" required value={formData.cvv} onChange={e => setFormData({
                    ...formData,
                    cvv: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FDB913] focus:border-[#FDB913]" placeholder="123" data-id="element-304" />
                  </div>
                </div>
              </div>

              <Button type="submit" size="lg" className="w-full mt-6" isLoading={processing} disabled={processing} data-id="element-305">
                {processing ? 'Processing...' : `Pay $${cartTotal.toLocaleString()}`}
              </Button>

              <p className="text-xs text-center text-gray-500 mt-4" data-id="element-306">
                🔒 Secure checkout • Test Mode
                <br data-id="element-307" />
                <span className="text-[#FDB913] font-medium" data-id="element-308">
                  Press Shift+P to auto-fill and bypass
                </span>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>;
}